"use client";

import React, { startTransition, useActionState, useEffect, useState } from "react";
import ConfirmModal from "../ui/ConfirmModal";
import { VIEWING_INITIAL_STATE } from "@/services/form.service";
import { CaseData, CMSData } from "@/cms/models";
import { ApplicationFraction } from "@/utils/gql/graphql";
import { submitViewingForm } from "@/actions/viewing-form";
import { formatDate } from "@/utils/helper";
import DateInput from "../ui/inputs/DateInput";
import TextInput from "../ui/inputs/TextInput";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import FormLayout from "../ui/inputs/FormLayout";
import { useReCaptcha } from "next-recaptcha-v3";

const ViewingForm: React.FC<{
  caseData: CaseData;
  appData: ApplicationFraction;
  cmsData: CMSData;
}> = ({ appData, cmsData, caseData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [formState, formAction, isPending] = useActionState(
    (prevState: any, formData: FormData) => {
      formData.append("caseId", caseData!.id!);
      return submitViewingForm(prevState, formData);
    },
    VIEWING_INITIAL_STATE
  );
  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    console.log("Recaptcha:", { enabled: formState.recaptchaIsEnabled, validToken: formState.recaptchaIsValid })

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true)
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);
  const {
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    message,
    time,
    date,
    confirmedDate,
    confirmedTime,
  } = formState?.data || {};

  const { executeRecaptcha } = useReCaptcha();
  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await executeRecaptcha("AssessmentForm");
        payload.append("recaptchaToken", token)
      } catch (error) {
        console.log("[ReCaptcha Error]:", error)
        payload.delete("recaptchaToken")
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  }

  return (
    <div className="">
      <ConfirmModal
        app={appData}
        description="Får du brug for at ændre, justere eller aflyse din bestilling, kan du kontakte os på:"
        title="Du har anmodet om en fremvisning"
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
      >
        <div className="flex flex-col">
          <div className="homepage-text-sm mb-3">
            <div>
              Dato:{" "}
              {[formatDate(confirmedDate), confirmedTime]
                .filter((x) => !!x)
                .join(" klokken: ")}
            </div>
            <div>{caseData!.displayName}</div>
          </div>
          <div>
            Vi bestræber os på at imødekomme dit ønske. Kan det ønskede
            tidspunkt ikke lade sig gøre, finder vi et nyt sammen med dig.
          </div>
        </div>
      </ConfirmModal>
      <ConfirmModal
        app={null}
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />
      <div className="flex flex-col gap-4 flex-1 w-full">
        <FormLayout
          submitLabel="Bestil"
          featureFlags={cmsData?.featureFlags}
          isPending={isPending}
          formAction={submit}>
          <DateInput
            cmsData={cmsData}
            name="date"
            label="Dato"
            defaultValue={date}
            errors={formState?.zodErrors?.date}
            required={true}
          />
          <TextInput
            cmsData={cmsData}
            name="time"
            label="Ønsket tidspunkt på dagen"
            defaultValue={time}
            errors={formState?.zodErrors?.time}
            required={false}
          />
          <TextInput
            cmsData={cmsData}
            name="firstName"
            label="Fornavn"
            defaultValue={firstName}
            errors={formState?.zodErrors?.firstName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            name="lastName"
            label="Efternavn"
            defaultValue={lastName}
            errors={formState?.zodErrors?.lastName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-6"
            name="address"
            label="Adresse"
            defaultValue={address}
            errors={formState?.zodErrors?.address}
            required={cmsData?.forms.addressRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-2"
            name="zip"
            label="Postnr"
            defaultValue={zip}
            errors={formState?.zodErrors?.zip}
            required={cmsData?.forms.zipRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-4"
            name="city"
            label="By"
            defaultValue={city}
            errors={formState?.zodErrors?.city}
            required={cmsData?.forms.cityRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="phone"
            label="Telefonnummer"
            defaultValue={phone}
            errors={formState?.zodErrors?.phone}
            required={cmsData?.forms.phoneRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="email"
            label="E-mail"
            defaultValue={email}
            errors={formState?.zodErrors?.email}
            required={cmsData?.forms.emailRequired}
          />
          <TextAreaInput
            cmsData={cmsData}
            name="message"
            label="Besked"
            defaultValue={message}
            errors={formState?.zodErrors?.message}
            required={false}
          />
        </FormLayout>
      </div>
    </div>
  );
};

export default ViewingForm;
